import { Chip, Stack } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import React from 'react';

/**
 * Per-topic accent colours. Unlisted topics fall back to a neutral so a new
 * topic never renders as an unreadable default.
 */
const TOPIC_COLORS: Record<string, string> = {
  poetry: '#ab47bc',
  travel: '#29b6f6',
  life: '#66bb6a',
  technology: '#26c6da',
  politics: '#ef5350',
};

const NEUTRAL = '#90a4ae';

const topicColor = (topic: string): string => TOPIC_COLORS[topic.toLowerCase()] ?? NEUTRAL;

// Tinted outline rather than a solid fill: at row scale a block of saturated
// colour competes with the title for attention.
const TopicChip = styled(Chip, {
  shouldForwardProp: (p) => p !== '$accent',
})<{ $accent: string }>(({ $accent }) => ({
  borderColor: alpha($accent, 0.5),
  backgroundColor: alpha($accent, 0.12),
  color: $accent,
  fontWeight: 600,
  letterSpacing: '0.02em',
}));

type TopicChipsProps = {
  topics: string[];
  size?: 'small' | 'medium';
  /** Lay the chips out in a column (blog list row) instead of a wrapping row. */
  direction?: 'row' | 'column';
};

const TopicChips: React.FC<TopicChipsProps> = ({ topics, size = 'small', direction = 'row' }) => {
  if (!topics?.length) return null;

  return (
    <Stack
      direction={direction}
      spacing={0.75}
      flexWrap={direction === 'row' ? 'wrap' : 'nowrap'}
      useFlexGap
      alignItems={direction === 'column' ? 'flex-start' : 'center'}
    >
      {topics.map((topic) => (
        <TopicChip
          key={topic}
          $accent={topicColor(topic)}
          label={topic}
          size={size}
          variant="outlined"
        />
      ))}
    </Stack>
  );
};

export default TopicChips;
